import { useEffect, useState } from 'react';
import type { PaymentMethod } from '@techgitter/contracts';
import { fetchPaymentMethods } from '../services/paymentMethodsApi';

// Module-level cache. Payment methods are a small RS-mirrored reference
// list that changes rarely; we fetch once per page load and serve all
// callers from the cache. Same pattern as useRepairServiceTypes — see
// invalidatePaymentMethodsCache() to force a refresh after admin edits.

let cache: PaymentMethod[] | null = null;
let inflight: Promise<PaymentMethod[]> | null = null;
const subscribers = new Set<() => void>();

const getMethods = (): Promise<PaymentMethod[]> => {
  if (cache !== null) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchPaymentMethods()
      .then((items) => {
        cache = items;
        return items;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

export function invalidatePaymentMethodsCache(): void {
  cache = null;
  inflight = null;
  subscribers.forEach((notify) => {
    try {
      notify();
    } catch {
      /* ignore */
    }
  });
}

export interface UsePaymentMethodsResult {
  methods: PaymentMethod[];
  isLoading: boolean;
  error: string | null;
}

export function usePaymentMethods(): UsePaymentMethodsResult {
  const [methods, setMethods] = useState<PaymentMethod[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState(cache == null);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setRev((r) => r + 1);
    subscribers.add(onInvalidate);
    return () => {
      subscribers.delete(onInvalidate);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (cache != null) {
      setMethods(cache);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setIsLoading(true);
    getMethods()
      .then((items) => {
        if (!cancelled) setMethods(items);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rev]);

  return { methods, isLoading, error };
}
